import React, { useState, useEffect, useCallback } from 'react';
import { useSettingsConfig } from '../../SettingsProvider/SettingsConfigProvider';
import { getCategoryById } from '../../config/SettingsCategories';
import { VersionInfo } from '../VersionInfo/VersionInfo';
import { SettingsCategoryPanel } from '../SettingsCategoryPanel/SettingsCategoryPanel';
import './SettingsLayout.css';
import { SearchControl } from '../SearchControl/SearchControl';
import { SettingsCategory } from '../../config/SettingsCategory';
import { useCategoryVisibility } from '../../hooks/useCategoryVisibility';
import { SettingsEntry } from '../../Contracts/base';
import { FlyoverProvider } from '../FlyoverNotification/FlyoverNotificationContext';
import { getLinkedUrl } from '../../config/LinkedUrlsConfig';
import { SUPPORT_AND_TROUBLESHOOT_SETTING_IDS } from '../../config/SettingIds';
import { SettingsNativeBridgeFactory } from '../../bridge/SettingsNativeBridgeFactory';
import { ProgressiveLoader } from '../ProgressiveLoader/ProgressiveLoader';

export const SettingsLayout: React.FC = () => {
	const { error, settingsCategories, entrypoint } = useSettingsConfig();
	const { visibleCategories, isLoading: isCategoryVisibilityLoading } =
		useCategoryVisibility(settingsCategories);
	const [activeCategory, setActiveCategory] = useState<SettingsCategory | null>(null);
	const [searchQuery, setSearchQuery] = useState('');

	// Set the first visible category as active when visibility is determined
	useEffect(() => {
		if (
			!isCategoryVisibilityLoading &&
			visibleCategories.length > 0 &&
			activeCategory === null
		) {
			setActiveCategory(visibleCategories[0].id as SettingsCategory);
		}
	}, [isCategoryVisibilityLoading, visibleCategories, activeCategory]);

	const handleCategoryClick = useCallback((categoryId: SettingsCategory) => {
		if (categoryId === SettingsCategory.HelpDocumentation) {
			const url = getLinkedUrl(SUPPORT_AND_TROUBLESHOOT_SETTING_IDS.HELP_DOCUMENTATION);
			if (url) {
				window.open(url, '_blank', 'noopener,noreferrer');
				return;
			}
		}
		if (categoryId === SettingsCategory.ResetCWA) {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			bridge.setSetting({
				settingKey: SUPPORT_AND_TROUBLESHOOT_SETTING_IDS.RESET_CWA,
				settingValue: true,
			});
			return;
		}
		setActiveCategory(categoryId);
	}, []);

	const handleSearchChange = (query: string) => {
		setSearchQuery(query);
	};

	const renderContent = () => {
		if (error) {
			return (
				<div className="content-error">
					<p className="error-message">Error: {error}</p>
					<button className="retry-button" onClick={() => window.location.reload()}>
						Retry
					</button>
				</div>
			);
		}

		if (isCategoryVisibilityLoading) {
			return <ProgressiveLoader />;
		}

		if (activeCategory === null) {
			return (
				<div className="content-placeholder">
					<h2>No settings available</h2>
					<p>There are no settings available to configure.</p>
				</div>
			);
		}

		const activeSettings = getCategoryById(visibleCategories, activeCategory);
		if (activeSettings) {
			const ActiveComponent = activeSettings.component;
			return (
				<ActiveComponent
					title={activeSettings.title}
					description={activeSettings.description}
					categoryId={activeSettings.id}
					onNavigate={handleCategoryClick}
				/>
			);
		}

		return (
			<div className="content-placeholder">
				<h2>Select a settings category</h2>
				<p>Choose a category from the sidebar to configure your settings.</p>
			</div>
		);
	};

	return (
		<div className="settings-layout">
			<div className="settings-content">
				{/* Left Sidebar */}
				<div className="settings-sidebar">
					{/* Search Control - hidden for Account Management entrypoint */}
					{entrypoint !== SettingsEntry.ACCOUNT_MANAGEMENT && (
						<SearchControl
							searchQuery={searchQuery}
							onSearchChange={handleSearchChange}
							placeholder="Search settings..."
						/>
					)}

					{/* Settings Navigation Panel */}
					<SettingsCategoryPanel
						categories={visibleCategories}
						activeCategory={activeCategory}
						onCategoryClick={handleCategoryClick}
					/>

					{/* Version Information */}
					<VersionInfo />
				</div>
				{/* Main Content Area */}
				<div className="settings-main-content">
					<FlyoverProvider>
						<div className="settings-main-inner">{renderContent()}</div>
					</FlyoverProvider>
				</div>
			</div>
		</div>
	);
};
